import React from 'react';
import { MillBrief } from '../../lib/millsData';

interface ShedBriefCardProps {
  brief: MillBrief;
}

/**
 * Verified location-context tags rendered under the mill identity block.
 * Three structured tags (ecoregion · dominant species · forest-economy
 * character) plus an optional one-line "additional fact" footer. Sources
 * for every claim live in the per-mill research notes (commit history /
 * `getMillBrief` JSDoc); the audience is forestry-literate, so the tags
 * use formal terminology (EPA Level III ecoregions, common species names)
 * without inline glossing.
 *
 * Renders nothing if the mill has no brief fields populated — keeps the
 * panel quiet for placeholder mills that haven't been researched yet.
 */
export const ShedBriefCard: React.FC<ShedBriefCardProps> = ({ brief }) => {
  const hasAnyTag =
    brief.ecoregion ||
    (brief.dominantSpecies && brief.dominantSpecies.length > 0) ||
    brief.forestEconomyCharacter;
  if (!hasAnyTag && !brief.optionalNote) return null;

  return (
    <div className="flex flex-col gap-2 mt-3">
      {brief.ecoregion && (
        <BriefRow label="Ecoregion" value={brief.ecoregion} accent="lime" />
      )}
      {brief.dominantSpecies && brief.dominantSpecies.length > 0 && (
        <BriefRow
          label="Dominant species"
          value={brief.dominantSpecies.join(' · ')}
          accent="mint"
        />
      )}
      {brief.forestEconomyCharacter && (
        <BriefRow
          label="Forest economy"
          value={brief.forestEconomyCharacter}
          accent="lime"
        />
      )}
      {brief.optionalNote && (
        <p className="mt-1 text-[12px] font-roboto italic text-figma-ink-mute leading-relaxed">
          {brief.optionalNote}
        </p>
      )}
    </div>
  );
};

interface BriefRowProps {
  label: string;
  value: string;
  accent: 'lime' | 'mint';
}

// Compact label-value row. Label sits in mono uppercase mute, value in
// plain text against a subtle tinted background. Keeps the chrome quiet
// so several rows can stack in the panel header without dominating the
// supply-shed toggle / layer picker below.
const BriefRow: React.FC<BriefRowProps> = ({ label, value, accent }) => {
  const accentClass =
    accent === 'lime'
      ? 'bg-figma-lime/5 border-figma-lime/20'
      : 'bg-figma-mint/5 border-figma-mint/20';
  return (
    <div
      className={`flex flex-col gap-0.5 px-3 py-2 rounded border ${accentClass}`}
    >
      <span className="text-[10px] font-roboto-mono uppercase tracking-[0.10em] text-figma-ink-mute">
        {label}
      </span>
      <span className="text-[12px] font-roboto text-figma-ink-soft leading-snug">
        {value}
      </span>
    </div>
  );
};
